/* eslint-disable react/jsx-pascal-case */
import { useEffect, Suspense, lazy } from 'react';

// 기본 컴포넌트들 (자주 사용되는 것들 - 직접 import)
import ScrollToTop from './component/ScrollToTop';
import RouteChangeTracker from './RouteChangeTracker';

// 🚀 통합 Context Provider (중첩 제거 및 최적화)
import { AppContextProvider } from './contexts/AppContextProvider';

// 🚀 성능 최적화: 최초 진입점 4개만 직접 import
// - 로그인 안 했을 때: V4Start
// - 로그인 후 최초 진입점: V4Start, WellnessScreen, CS_new (StudyCafe → Molduit)
import V4Start from './pages/V4/StartV4';
import WellnessScreen from './pages/Wellness/WellnessScreen';
import CS_new from './pages/CS_new';

import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import jwt_decode from 'jsonwebtoken/decode';
import * as amplitude from '@amplitude/analytics-browser';
import { initializeAdrop } from './services/adropService';
// 🚀 타이머 보상 시스템 활성화 유틸리티 (전역 사용 가능)
import './utils/enableTimerReward';

// V4 로그인 플로우 (lazy loading)
const GyeongdeokFamily = lazy(() => import('./pages/V4/GyeongdeokFamily'));
const V4PhoneNumber = lazy(() => import('./pages/V4/PhoneNumberV4'));
const V4Password = lazy(() => import('./pages/V4/PasswordV4'));
const V4Sorry = lazy(() => import('./pages/V4/SorryV4'));
const V4UnderAge = lazy(() => import('./pages/V4/UnderAgeV4'));

// Wellness 서브 페이지들 (lazy loading)
const WellnessExchange = lazy(() => import('./pages/Wellness/WellnessExchange'));
const KakaoCallback = lazy(() => import('./pages/KakaoCallback'));
// import SleepAnalysis from './pages/Wellness/SleepAnalysis'; // 임시 비활성화

// Wellness 서브 페이지들 (lazy loading - 초기 번들 크기 감소)
const DailyEarning = lazy(() => import('./pages/Wellness/DailyEarning.tsx'));
const WellnessMission = lazy(() => import('./pages/Wellness/WellnessMission'));
// const WellnessQuiz = lazy(() => import('./pages/Wellness/WellnessQuiz.tsx')); // 임시 주석처리
// const DailyFortune = lazy(() => import('./pages/Wellness/DailyFortune.tsx')); // 임시 주석처리

// Shop 페이지들 (lazy loading)
const Shop = lazy(() => import('./pages/shop/pages/Shop'));
const ShopCart = lazy(() => import('./pages/shop/pages/Cart'));
const ShopMenu = lazy(() => import('./pages/shop/pages/Menu'));
const ShopProductDetail = lazy(() => import('./pages/shop/pages/ProductDetail'));
const ShopSettings = lazy(() => import('./pages/shop/pages/Settings'));
const ShopCheckout = lazy(() => import('./pages/shop/pages/Checkout'));

// 🚀 React Query 설정은 AppContextProvider로 이동 (중첩 제거)

// V4 덜 사용되는 페이지들 (lazy loading)
const V4UnsupportedCountry = lazy(() => import('./pages/V4/UnsupportedCountryV4'));
const V4SpartaFlow = lazy(() => import('./pages/V4/Sparta/SpartaFlowV4'));
const AdTest = lazy(() => import('./pages/AdTest'));
const MolduitComingSoon = lazy(() => import('./pages/Molduit/MolduitComingSoon'));

// 🚀 최적화: SuspenseWrapper를 컴포넌트 외부로 이동하여 재생성 방지
// 🚀 React SPA 최적화 원칙: 스켈레톤은 진짜 긴 로딩 시간에만 사용
// lazy loading 페이지 전환 시 스켈레톤 대신 투명 배경 사용 (깜빡임 방지)
const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<div style={{ minHeight: '100vh', backgroundColor: 'transparent' }} />}>
    {children}
  </Suspense>
);

function App() {

  // 🚀 최적화: useEffect 추가하여 토큰 체크, Amplitude 및 Adrop 초기화
  useEffect(() => {
    const checkTokenAndInitAmplitude = () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decodedToken = jwt_decode(token);
          const tokenUserID = decodedToken.user_id;
          const isApp = localStorage.getItem('app');

          // setTokenUserID((prevTokenUserID) => {
          //   return prevTokenUserID === tokenUserID ? prevTokenUserID : tokenUserID;
          // });
          amplitude.init("3e3ca2c5a4db2caee2881eeb2336bdca", tokenUserID, {
            defaultTracking: true
          });

          if (isApp === 'true') {
            window.amplitude('setUserProperties', { 'isApp': true });
          }

        } catch (error) {
        }
      } else {
        amplitude.init("3e3ca2c5a4db2caee2881eeb2336bdca", undefined, {
          defaultTracking: true
        });
      }
    };

    // 🚀 Adrop 초기화 (SDK 로드 대기 - defer 스크립트 대응)
    const initAdropWithRetry = (retryCount = 0) => {
      if (window.adrop) {
        initializeAdrop();
      } else if (retryCount < 10) {
        // SDK 로드 안됐으면 200ms 후 재시도 (최대 10회 = 2초)
        setTimeout(() => initAdropWithRetry(retryCount + 1), 200);
      }
    };

    checkTokenAndInitAmplitude();
    initAdropWithRetry();

    const storageListener = e => {
      if (e.key === 'token') {
        checkTokenAndInitAmplitude();
        // 🚨 UserContext에 토큰 변경 알림
        window.dispatchEvent(new Event('tokenChanged'));
      }
    };

    window.addEventListener('storage', storageListener);
    return () => window.removeEventListener('storage', storageListener);
  }, []);

  return (
    <AppContextProvider>
      <Router>
        <ScrollToTop />
        <RouteChangeTracker />
        <div className='App'>
        <Routes basename={process.env.PUBLIC_URL}>
          <Route path="/" element={<Navigate to="/V4/Start" replace />} />
          <Route path="/CS" element={<CS_new />} />
          
          {/* Legacy 경로 → V4로 리다이렉트 */}
          <Route path="/Start" element={<Navigate to="/V4/Start" replace />} />
          <Route path="/Store" element={<Navigate to="/V4/Start" replace />} />

          <Route path="/oauth/kakao/callback" element={
            <SuspenseWrapper>
              <KakaoCallback />
            </SuspenseWrapper>
          } />

          <Route path="/StudyCafe" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          <Route path="/Studycafe" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          
          {/* Legacy 경로 → V4로 리다이렉트 */}
          <Route path="/Sorry" element={<Navigate to="/V4/Sorry" replace />} />
          
          <Route path="/Wellness" element={<WellnessScreen />} />
          <Route path="/Wellness/Mission" element={
            <SuspenseWrapper>
              <WellnessMission />
            </SuspenseWrapper>
          } />
          <Route path="/Wellness/DailyEarning" element={
            <SuspenseWrapper>
              <DailyEarning />
            </SuspenseWrapper>
          } />
          {/* <Route path="/Wellness/Quiz" element={
            <SuspenseWrapper>
              <WellnessQuiz />
            </SuspenseWrapper>
          } /> */}
          {/* <Route path="/Wellness/Fortune" element={
            <SuspenseWrapper>
              <DailyFortune />
            </SuspenseWrapper>
          } /> */}
                {/* 임시 비활성화: <Route path="/Wellness/SleepAnalysis" element={<SleepAnalysis />} /> */}

          {/* ========== V4 페이지들 ========== */}
          {/* V4 기본 플로우 */}
          <Route path="/V4/Start" element={<V4Start />} />
          <Route path="/V4/Gyeongdeok" element={<SuspenseWrapper><GyeongdeokFamily /></SuspenseWrapper>} />
          <Route path="/V4/PhoneNumber" element={
            <SuspenseWrapper>
              <V4PhoneNumber />
            </SuspenseWrapper>
          } />
          <Route path="/V4/Password" element={
            <SuspenseWrapper>
              <V4Password />
            </SuspenseWrapper>
          } />
          <Route path="/V4/UnderAge" element={
            <SuspenseWrapper>
              <V4UnderAge />
            </SuspenseWrapper>
          } />
          <Route path="/V4/Sorry" element={
            <SuspenseWrapper>
              <V4Sorry />
            </SuspenseWrapper>
          } />
          <Route path="/V4/Unsupported" element={
            <SuspenseWrapper>
              <V4UnsupportedCountry />
            </SuspenseWrapper>
          } />

        {/* V4 StudyCafe → 몰두잇 커밍순 (다음 버전부터 별도 앱) */}
        <Route path="/V4/StudyCafe" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
        <Route path="/V4/StudyCafe_Main" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
        <Route path="/V4/StudyCafe_Timer" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
        <Route path="/V4/StudyCafe_Scholarship" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
        <Route path="/V4/StudyCafe_Exchange" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          
          {/* V4 StudyCafe 직접 링크 → 몰두잇 커밍순 */}
          <Route path="/StudycafeV4_Main" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          <Route path="/StudycafeV4_Timer" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          <Route path="/StudycafeV4_Scholarship" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          <Route path="/StudycafeV4_NewScholarship" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />
          <Route path="/StudycafeV4_Exchange" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />

          <Route path="/V5/StudyCafe_Main" element={<SuspenseWrapper><MolduitComingSoon /></SuspenseWrapper>} />

          {/* 몰두잇 커밍순 (다음 버전부터 별도 앱, 사전예약) */}
          <Route path="/Molduit" element={
            <SuspenseWrapper>
              <MolduitComingSoon />
            </SuspenseWrapper>
          } />
          <Route path="/V4/Molduit" element={
            <SuspenseWrapper>
              <MolduitComingSoon />
            </SuspenseWrapper>
          } />

          {/* V4 Wellness (기존 웰니스 컴포넌트 재사용) */}
          <Route path="/V4/Wellness" element={<WellnessScreen />} />
          
          {/* Wellness 교환소 */}
          <Route path="/Wellness/Exchange" element={
            <SuspenseWrapper>
              <WellnessExchange />
            </SuspenseWrapper>
          } />

          {/* V4 Sparta (새로운 V4 스파르타 컴포넌트) */}
          <Route path="/V4/Sparta" element={<V4SpartaFlow />} />


          {/* 관리자 전용 광고 테스트 페이지 */}
          <Route path="/ad-test" element={
            <SuspenseWrapper>
              <AdTest />
            </SuspenseWrapper>
          } />

          {/* 테스트용 Wellness 페이지들 */}
          <Route path="/test-wellness" element={<WellnessScreen />} />
          {/* <Route path="/test-sleep-analysis" element={<SleepAnalysis />} /> */} {/* 임시 주석처리 */}

          {/* Shop 페이지들 */}
          <Route path="/shop" element={
            <SuspenseWrapper>
              <Shop />
            </SuspenseWrapper>
          } />
          <Route path="/shop/cart" element={
            <SuspenseWrapper>
              <ShopCart />
            </SuspenseWrapper>
          } />
          <Route path="/shop/menu" element={
            <SuspenseWrapper>
              <ShopMenu />
            </SuspenseWrapper>
          } />
          <Route path="/shop/product/:id" element={
            <SuspenseWrapper>
              <ShopProductDetail />
            </SuspenseWrapper>
          } />
          <Route path="/shop/settings" element={
            <SuspenseWrapper>
              <ShopSettings />
            </SuspenseWrapper>
          } />
          <Route path="/shop/checkout" element={
            <SuspenseWrapper>
              <ShopCheckout />
            </SuspenseWrapper>
          } />

        </Routes>
      </div>
      
      {/* 개발 환경에서만 표시되는 포인트 디버그 패널 */}
      
      </Router>
    </AppContextProvider>
  );
}

export default App;